import { useFormik } from "formik";
import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import { Link, useNavigate } from "react-router";
import { useReopenedStages } from "../../../../hooks/useReopenedStages";
import { useAppDispatch as useDispatch, useAppSelector as useSelector } from "../../../../store";
import { hideTransparentPreloaderAction, showNotificationAction, showTransparentPreloaderAction } from "../../../../store/application/slice";
import { TUploadedDocumentsData } from "../../../../store/onboarding/types";
import { clearStage2ReopenThunk } from "../../../../store/superadmin/thunks";
import { ICandidateReviewFormInput } from "../../../../store/superadmin/types";
import { updateDocumentDateThunk } from "../../../../store/uploads/thunks";
import { TFileUploadsResponse } from "../../../../store/uploads/types";
import {
  formatDateToReadable,
  getDateLabel,
  getDocumentDate,
  getDocumentName,
  getMinDateByDocumentType,
  getValidityMonths,
  isIssuedType,
  isOneMonthBefore,
} from "../../../../utils/documentHelper";
import { isSuperAdmin } from "../../../../utils/roleHelper";
import { getLatestStage2Action } from "../../../../utils/stage2ActionHelper";
import SaveNotification from "../../../SaveNotification";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Download from "../../../UI/Icons/Download";
import RoundedTick from "../../../UI/Icons/RoundedTick";
import Warning from "../../../UI/Icons/Warning";
import CustomDateInput from "../../../UI/Inputs/DateInput";
import DocumentUpload from "../../../UI/Inputs/DocumentUpload";
import ErrorMessage from "../../../UI/Tooltips/ErrorMessage";
import ViewReferroDetails from "../CandidateReviewModal/Popup/ViewReferroDetails";

const ComplianceCheckEdit: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const reviewCandidateData = useSelector(
    (state) => state.superadmin.reviewCandidateData
  );

  const candidateVerificationStatus = reviewCandidateData?.profile.verification_status;
  // const lastAction = reviewCandidateData?.profile?.profile_steps[0]?.action;
  const reviewCandidateDocumentList = reviewCandidateData?.documents || [];
  const expiringDocumentsCollections = reviewCandidateData?.documents_expire_one_month_documents;
  const isDocumentGoingToExpire = reviewCandidateData?.documents_expire_one_month;
  const isDocumentExpired = reviewCandidateData?.is_documents_expired;
  const currentStage = reviewCandidateData?.profile?.current_stage ?? 0;

  const [showReferroDetailsModal, setShowReferroDetailsModal] = useState<boolean>(false);
  const [pdfDocument, setPdfDocument] = useState<string>("");
  const [showNotification, setShowNotification] = useState<boolean>(false);
  const [pcValidUntil, setPcValidUntil] = useState<Date | null>(null);
  const [wccValidUntil, setWccValidUntil] = useState<Date | null>(null);

  const user = useSelector((state) => state.auth.user);
   const isAdmin = isSuperAdmin(user);

  const { isSectionReopenedFn } = useReopenedStages();
  const isAdminCompleteReopened = isSectionReopenedFn("admin_complete");

  const matchedMultipleTitles: string[] = [];
  const expiredTitles: string[] = [];

  const documentLabels: Record<string, string> = {
    police_check: "Police Check",
    working_with_children_check: "Working With Children Check / Blue Card",
  };

  expiringDocumentsCollections?.forEach((doc) => {
    const match = ["police_check", "working_with_children_check"].find(
      (config) => config === doc.document_type
    );

    if (!match) return;

    const title = documentLabels[match];
    const isExpired = doc.is_expired === 1;

    if (isExpired) {
      expiredTitles.push(title);
    } else {
      matchedMultipleTitles.push(title);
    }
  });

  const formattedExpired = expiredTitles.join(", ");
  const formattedMultipleTitles = matchedMultipleTitles.join(", ");

  const getFileName = (type: string) => {
    const reviewDocument = reviewCandidateDocumentList.find(
      (file: TUploadedDocumentsData) => file.document_type === type
    );
    return reviewDocument?.file_name || "";
  };

  const getFileUrl = (type: string) => {
    const reviewDocument = reviewCandidateDocumentList.find(
      (file: TUploadedDocumentsData) => file.document_type === type
    );
    return reviewDocument?.file_path || null;
  };

  const validDocumentTypes = [
    "police_check",
    "working_with_children_check",
    "right_to_work",
    "reference_check",
    "reference_check_1",
  ];

  const policeCheck = reviewCandidateData?.documents?.find(
    (doc: TUploadedDocumentsData) => doc.document_type === "police_check"
  );
  const wccComplianceCheck = reviewCandidateData?.documents?.find(
    (doc: TUploadedDocumentsData) =>
      doc.document_type === "working_with_children_check"
  );

  const latestStage2Action = getLatestStage2Action(
    reviewCandidateData?.profile?.profile_steps ?? [],
    reviewCandidateData?.reopened_stages_status,
    reviewCandidateData?.referoo_candidate?.created_at
  );

  const lastAction = latestStage2Action;

  const pc_valid_until_str = policeCheck?.issued_date;
  const wcc_valid_until_str = wccComplianceCheck?.validity_date;

  const hasWWCDoc = reviewCandidateDocumentList?.some((doc) => doc.document_type === "working_with_children_check");
  const hasPoliceCheck = reviewCandidateDocumentList?.some((doc) => doc.document_type === "police_check");
  const hasRighttoWork = reviewCandidateDocumentList?.some((doc) => doc.document_type === "right_to_work");
  const hasReferenceCheck = reviewCandidateDocumentList?.some((doc) => doc.document_type === "reference_check") && reviewCandidateDocumentList?.some((doc) => doc.document_type === "reference_check_1");

  const isSaveCHnageButtonDisabled = (): boolean => {

    // Stage 2 compliance can no longer be bypassed: every compliance file is required
    return !(hasWWCDoc && hasPoliceCheck && hasRighttoWork && hasReferenceCheck);

  }

  const { setFieldValue } = useFormik<ICandidateReviewFormInput>({
    enableReinitialize: true,
    initialValues: {
      candidate_id: reviewCandidateData?.profile?.id ?? 0,
      step: 2,
      action: "",
      bypass_compliance_check:
        reviewCandidateData?.profile?.bypass_compliance_check ?? 0,
      bypass_online_interview: 0,
      bypass_id_verification: 0,
      id_verification_bypass_reason: null,
      online_interview_bypass_reason: null,
      complaince_check_bypass_reason:
        reviewCandidateData?.profile?.step_bypass_reason?.find(
          (type) => type.bypass_type === "bypass_compliance_check"
        )?.reason ?? null,
    },
    onSubmit: () => {},
  });

  const inProgress = useSelector((state) => state.superadmin.saveBypassInProgress);
  // const isDisabled = !reviewCandidateData?.profile?.id || isBypassCheckboxDisabled() || inProgress;
  const isDisabled = false;

  const handleSubmit = async () => {
    dispatch(showTransparentPreloaderAction())

    // All required documents are uploaded (Save is disabled otherwise), so clear the stage 2 reopen status
    if (reviewCandidateData && hasWWCDoc && hasPoliceCheck && hasRighttoWork && hasReferenceCheck) {
      await dispatch(clearStage2ReopenThunk({
        candidate_id: reviewCandidateData.profile.id,
        candidate_uuid: reviewCandidateData.uuid,
      }));
    }

    dispatch(showNotificationAction("Updated"))

    dispatch(hideTransparentPreloaderAction())
  };



  useEffect(() => {
    const pc_valid_until =
      pc_valid_until_str && pc_valid_until_str !== ""
        ? new Date(pc_valid_until_str)
        : null;

    const wcc_valid_until =
      wcc_valid_until_str && wcc_valid_until_str !== ""
        ? new Date(wcc_valid_until_str)
        : null;

    if (!wccComplianceCheck) {
      setWccValidUntil(null);
    } else {
      setWccValidUntil(wcc_valid_until);
    }

    if (!policeCheck) {
      setPcValidUntil(null);
    } else {
      setPcValidUntil(pc_valid_until);
    }
  }, [wccComplianceCheck, policeCheck, pc_valid_until_str, wcc_valid_until_str]);

  const handleDocDate = (type: string, val: Date | null) => {
    if (!val) return;

    const year = val.getFullYear();
    const month = String(val.getMonth() + 1).padStart(2, "0");
    const day = String(val.getDate()).padStart(2, "0");

    const dateOnly = `${year}-${month}-${day}`;
    dispatch(
      updateDocumentDateThunk({
        document_type: type,
        date: dateOnly,
        candidate_uuid: reviewCandidateData?.uuid,
        isAdmin: true,
      })
    );
  };

  const clearStage2CalledRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isAdminCompleteReopened) {
      clearStage2CalledRef.current = false;
    }
  }, [isAdminCompleteReopened]);

  useEffect(() => {
    if (!reviewCandidateData) return;

    const docs = reviewCandidateData?.documents || [];
    const requiredTypes = [
      "police_check",
      "working_with_children_check",
      "right_to_work",
      "reference_check",
      "reference_check_1",
    ];

    const allUploaded = requiredTypes.every((type) => {
      const doc = docs.find((d) => d.document_type === type);
      return doc?.file_name && doc.file_name !== "";
    });

    const pcDoc = docs.find((d) => d.document_type === "police_check");
    const wccDoc = docs.find(
      (d) => d.document_type === "working_with_children_check"
    );
    const hasPcDate = !!pcDoc?.issued_date;
    const hasWccDate = !!wccDoc?.validity_date;

    if (allUploaded && hasPcDate && hasWccDate && isAdminCompleteReopened) {
      clearStage2CalledRef.current = true;
      dispatch(
        clearStage2ReopenThunk({
          candidate_id: reviewCandidateData.profile.id,
          candidate_uuid: reviewCandidateData.uuid,
        })
      );
    }
  }, [
    reviewCandidateData?.documents,
    isAdminCompleteReopened,
    reviewCandidateData?.profile?.id,
  ]);

  const handleFileSelect = (file: TFileUploadsResponse, fileName: string) => {
    setFieldValue(file.data.document_type + "_doc", file.data.resume);
    setFieldValue(file.data.document_type + "_doc_name", fileName);
  };

  const onClickClose = () => {
    setShowReferroDetailsModal(false);
    setPdfDocument("");
  };

  const complianceCheckDocs = reviewCandidateDocumentList?.filter((doc) =>
    validDocumentTypes.includes(doc.document_type)
  );

  const sortedComplianceCheckDocs = [...complianceCheckDocs].sort((a, b) => {
    const indexA = validDocumentTypes.indexOf(a.document_type);
    const indexB = validDocumentTypes.indexOf(b.document_type);

    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return 0;
  });

  return (
    <>
      {showNotification && (
        <SaveNotification
          message={"No Documents Found"}
          onClose={() => setShowNotification(false)}
        />
      )}

      <ViewReferroDetails
        showReferroDetailsModal={showReferroDetailsModal}
        onClickClose={onClickClose}
        pdfDocument={pdfDocument}
      />

      <div className="d-flex flex-column gap-20 fw-400 text-black">
        <div className="d-flex flex-column gap-15">
            <div className="d-flex align-items-center gap-20 justify-content-between">       
                <div className="fs-22">Compliance Check</div>
                {
                    isAdmin && (
                    <CustomButton
                        isLoading={inProgress}
                        disabled={isSaveCHnageButtonDisabled()}
                        contentClass="d-flex"
                        type="submit"
                        variant="primary"
                        className="btn-theme bg-teal text-black"
                        onClick={handleSubmit}
                    >
                        <RoundedTick color={isSaveCHnageButtonDisabled() ? "#757575" : "black"}/>
                        Save Changes
                    </CustomButton>
                    )
                }
            </div>
          <div className="fs-16">Admin to complete this step on behalf of employee.</div>
        </div>

        

        

        {/* Render the Compliance Check Documents */}
        {candidateVerificationStatus !== "verified" ||
        (candidateVerificationStatus === "verified" &&
          (isDocumentGoingToExpire || isDocumentExpired)) ? (
          <>
            {/* Police Check */}
            <div className="d-flex flex-column gap-10">
              <div className="fs-16 fw-700">Police Check</div>
              {lastAction === "admin-complete" &&
                !policeCheck && (
                  <div className="text-grey-dark fs-13">
                    Document not uploaded to Referoo yet
                  </div>
                )}
              {lastAction === "admin-complete" && (
                <>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"police_check"}
                    candidate_uuid={reviewCandidateData?.uuid}
                    uploadedFileName={getFileName("police_check")}
                    fileUrl={getFileUrl("police_check")}
                    openInNewTab={true}
                  />
                  <div className="d-flex gap-10 align-items-center">
                    <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                      Issue date:
                    </div>
                    <div className="min-w-200px">
                      <DatePicker
                        selected={policeCheck ? pcValidUntil : null}
                        onChange={(date: Date | null) => {
                          setPcValidUntil(date);
                          handleDocDate("police_check", date);
                        }}
                        customInput={
                          <CustomDateInput placeholder="Select a date..." />
                        }
                        dateFormat="dd MMM yyyy"
                        minDate={getMinDateByDocumentType("police_check")}
                        maxDate={new Date()}
                        disabled={!policeCheck}
                        showYearDropdown
                        scrollableYearDropdown
                        yearDropdownItemNumber={15}
                      />
                    </div>
                    <div className="d-flex flex-row fs-13 fw-400 text-grey-dark gap-3">
                      <Warning width={"15"} height={"15"} color={"#464646"} />
                      Expires {getValidityMonths("police_check")} months after
                      issue date and will need renewing
                    </div>
                  </div>
                </>
              )}
            </div>
            {policeCheck && !pc_valid_until_str && (
              <ErrorMessage
                className="align-items-center gap-3 lh-1px"
                message="Please select the date this document was issued"
              />
            )}

            <div className="bg-off-white min-h-1px"></div>

            {/* Working With Children Check */}
            <div className="d-flex flex-column gap-10">
              <div className="fs-16 fw-700">
                Working With Children Check / Blue Card
              </div>
              <DocumentUpload
                onFileSelect={handleFileSelect}
                type={"working_with_children_check"}
                candidate_uuid={reviewCandidateData?.uuid}
                uploadedFileName={getFileName("working_with_children_check")}
                fileUrl={getFileUrl("working_with_children_check")}
                openInNewTab={true}
                removeIcon={
                  wccComplianceCheck?.uploaded_by === "candidate" ? true : false
                }
              />
              <div className="d-flex gap-10 align-items-md-center flex-column flex-md-row">
                <div className="fs-13 fw-400 text-grey-dark text-nowrap">
                  Valid until:
                </div>
                <div className="min-w-200px">
                  <DatePicker
                    selected={wccComplianceCheck ? wccValidUntil : null}
                    onChange={(date: Date | null) => {
                      setWccValidUntil(date);
                      handleDocDate("working_with_children_check", date);
                    }}
                    customInput={
                      <CustomDateInput placeholder="Select a date..." />
                    }
                    dateFormat="dd MMM yyyy"
                    minDate={new Date()}
                    disabled={
                      !wccComplianceCheck ||
                      (wccComplianceCheck &&
                        wccComplianceCheck?.uploaded_by === "candidate" &&
                        wcc_valid_until_str)
                        ? true
                        : false
                    }
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={15}
                  />
                </div>
              </div>
            </div>
            {wccComplianceCheck && !wcc_valid_until_str && (
              <ErrorMessage
                className="align-items-center gap-3 lh-1px"
                message="Please select the date this document is valid until"
              />
            )}

            <div className="bg-off-white min-h-1px"></div>

            {/* Right To Work */}
            <div className="d-flex flex-column gap-10">
              <div className="fs-16 fw-700">Right To Work</div>
              {lastAction === "admin-complete" &&
                !reviewCandidateDocumentList?.find(
                  (doc) => doc.document_type === "right_to_work"
                ) && (
                  <div className="text-grey-dark fs-13">
                    Document not uploaded to Referoo yet
                  </div>
                )}
              {lastAction === "admin-complete" && (
                <DocumentUpload
                  onFileSelect={handleFileSelect}
                  type={"right_to_work"}
                  candidate_uuid={reviewCandidateData?.uuid}
                  uploadedFileName={getFileName("right_to_work")}
                  fileUrl={getFileUrl("right_to_work")}
                  openInNewTab={true}
                />
              )}
            </div>

            <div className="bg-off-white min-h-1px"></div>

            {/* Reference Check */}
            <div className="d-flex flex-column gap-10">
              <div className="fs-16 fw-700">Reference Check</div>
              {lastAction === "admin-complete" &&
                !reviewCandidateDocumentList?.find(
                  (doc) => doc.document_type === "reference_check"
                ) &&
                !reviewCandidateDocumentList?.find(
                  (doc) => doc.document_type === "reference_check_1"
                ) && (
                  <div className="text-grey-dark fs-13">
                    Documents not uploaded to Referoo yet
                  </div>
                )}
              {lastAction === "admin-complete" && (
                <>
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"reference_check"}
                    candidate_uuid={reviewCandidateData?.uuid}
                    uploadedFileName={getFileName("reference_check")}
                    fileUrl={getFileUrl("reference_check")}
                    openInNewTab={true}
                  />
                  <DocumentUpload
                    onFileSelect={handleFileSelect}
                    type={"reference_check_1"}
                    candidate_uuid={reviewCandidateData?.uuid}
                    uploadedFileName={getFileName("reference_check_1")}
                    fileUrl={getFileUrl("reference_check_1")}
                    openInNewTab={true}
                  />
                </>
              )}
            </div>
          </>
        ) : (
          <>
            {sortedComplianceCheckDocs.map(
              (doc, idx: number, filteredList) => (
                <React.Fragment key={idx}>
                  <div className="d-flex flex-column gap-10">
                    {doc.document_type !== "reference_check_1" && (
                      <div className="fs-13 fw-700 text-black text-capitalize">
                        {getDocumentName(doc.document_type)}
                      </div>
                    )}
                    <div className="d-flex">
                      <Link
                        to={doc.file_path}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <CustomButton
                          variant={"primary"}
                          type="button"
                          className="btn-theme bg-off-white text-black"
                        >
                          <Download />
                          {doc.file_name}
                        </CustomButton>
                      </Link>
                    </div>
                    {![
                      "right_to_work",
                      "reference_check",
                      "reference_check_1",
                    ].includes(doc.document_type) && (
                      <div className="d-flex flex-row gap-5">
                        <div className="fs-13 fw-400 text-grey-dark">
                          {getDateLabel(doc.document_type)}:{" "}
                        </div>
                        <span
                          className={`fs-13 ${
                            isOneMonthBefore(doc?.validity_date ?? "")
                              ? `fw-700 text-alert`
                              : `fw-400 text-grey-dark`
                          }`}
                        >
                          {isIssuedType(doc.document_type) ? (
                            <>
                              {formatDateToReadable(doc.issued_date) ||
                                "No data"}{" "}
                              | Valid until:{" "}
                              <span
                                className={`${
                                  isOneMonthBefore(doc.validity_date)
                                    ? "fw-700 text-alert"
                                    : "fw-400 text-grey-dark"
                                }`}
                              >
                                {formatDateToReadable(doc.validity_date) ||
                                  "No data"}
                              </span>
                            </>
                          ) : (
                            formatDateToReadable(getDocumentDate(doc)) ||
                            "No data"
                          )}
                        </span>
                      </div>
                    )}
                  </div>
                  {idx < filteredList.length - 1 &&
                    doc.document_type !== "reference_check" && (
                      <div className="bg-off-white min-h-1px max-w-484px"></div>
                    )}
                </React.Fragment>
              )
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ComplianceCheckEdit;
